import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { fetchGameEvents } from "@/lib/edgeFunctions";

export interface GameEvent {
  id: string;
  fixture_id: number;
  game_id: string | null;
  time_elapsed: number;
  time_extra: number | null;
  team_id: string | null;
  player_id: number | null;
  player_name: string | null;
  event_type: string;
  event_detail: string | null;
  comments: string | null;
  assist_id: number | null;
  assist_name: string | null;
  created_at: string;
  updated_at: string;
  team?: {
    id: string;
    name: string;
    logo_url: string;
  };
}

// 🛡️ Frontend defensive deduplication (7-field key + substitution special handling + time_extra corrections)
const deduplicateEvents = (events: GameEvent[]): GameEvent[] => {
  const eventMap = new Map<string, GameEvent>();
  
  events.forEach(event => {
    // 🔄 SPECIAL: Substitutions - deduplicate by player + assist (ignore time_extra)
    if (event.event_type === 'subst' && event.assist_id) {
      const substKey = `${event.fixture_id}-subst-${event.player_id}-${event.assist_id}`;
      const existing = eventMap.get(substKey);
      
      // Keep most recent (by updated_at) - handles API time corrections (90+2' → 90+3')
      if (!existing || new Date(event.updated_at) > new Date(existing.updated_at)) {
        eventMap.set(substKey, event);
      }
      return; // Skip 7-field dedup for substitutions
    }
    
    // 🎯 SPECIAL: Goals & Cards - also use key WITHOUT time_extra (defensive against API corrections)
    // Ex: R. Orsolini Goal 45+1' → 45+3' should be deduplicated
    if ((event.event_type === 'Goal' || event.event_type === 'Card') && event.player_id && event.player_id > 0) {
      const normalizedTeamId = event.team_id || '00000000-0000-0000-0000-000000000000';
      const normalizedEventDetail = event.event_detail ?? '';
      
      // Key WITHOUT time_extra (ignores API time corrections)
      const keyWithoutExtra = `${event.fixture_id}-${event.time_elapsed}-${event.event_type}-${normalizedTeamId}-${event.player_id}-${normalizedEventDetail}`;
      
      const existing = eventMap.get(keyWithoutExtra);
      
      // Keep event with HIGHER time_extra (45+3 > 45+1), or most recent
      if (!existing || 
          (event.time_extra ?? 0) > (existing.time_extra ?? 0) ||
          ((event.time_extra ?? 0) === (existing.time_extra ?? 0) && new Date(event.updated_at) > new Date(existing.updated_at))) {
        eventMap.set(keyWithoutExtra, event);
      }
      return; // Skip 7-field dedup for Goals/Cards
    }
    
    // For other events: use 7-field logic with time_extra
    const normalizedTeamId = event.team_id || '00000000-0000-0000-0000-000000000000';
    const normalizedPlayerId = event.player_id ?? -1;
    const normalizedTimeExtra = event.time_extra ?? 0;
    const normalizedEventDetail = event.event_detail ?? '';
    
    const key = `${event.fixture_id}-${event.time_elapsed}-${normalizedTimeExtra}-${event.event_type}-${normalizedTeamId}-${normalizedPlayerId}-${normalizedEventDetail}`;
    
    const existing = eventMap.get(key);
    
    if (!existing || new Date(event.updated_at) > new Date(existing.updated_at)) {
      eventMap.set(key, event);
    }
  });
  
  return Array.from(eventMap.values());
};

// 🧹 Remove phantom coach cards (API sends duplicates with player_id=-1 and no name)
const cleanPhantomCoachCards = (events: GameEvent[]): GameEvent[] => {
  return events.filter(event => {
    if (event.event_type !== 'Card') return true;
    if (event.player_id && event.player_id > 0) return true;
    
    // Phantom card (player_id <= 0 or null) without a name → always API noise
    if (!event.player_name) return false;
    
    // ✅ Keep if has player_name (real coach like T. Margarido)
    return true;
  });
};

export const useGameEvents = (fixtureId: number | null, enableRealtime = false, gameStatus?: string, enablePolling = false) => {
  const [hasFetched, setHasFetched] = useState(false);
  
  const query = useQuery({
    queryKey: ['game-events', fixtureId],
    queryFn: async () => {
      if (!fixtureId) return [];

      const { data, error } = await supabase
        .from('game_events')
        .select('*, team:teams(id, name, logo_url)')
        .eq('fixture_id', fixtureId)
        .order('time_elapsed', { ascending: true });

      if (error) {
        console.error('Error fetching game events:', error);
        throw error;
      }

      // 🛡️ Apply defensive deduplication to prevent cached duplicates
      const rawEvents = data as GameEvent[];
      const dedupedEvents = deduplicateEvents(rawEvents);
      
      // 🧹 Remove phantom coach cards (API sends duplicates with player_id=-1)
      const cleanedEvents = cleanPhantomCoachCards(dedupedEvents);
      
      const totalRemoved = rawEvents.length - cleanedEvents.length;
      if (totalRemoved > 0) {
        console.warn(`🛡️ Frontend cleanup: ${rawEvents.length} → ${cleanedEvents.length} events (removed ${totalRemoved} duplicates/phantoms)`);
      }

      return cleanedEvents;
    },
    enabled: !!fixtureId,
    staleTime: 30000, // 30 seconds
    refetchInterval: false, // 📡 FASE 3: Updates via Realtime v2 + broadcast
  });

  // Real-time updates now handled by unified channel in AppLayout (useRealtimeSync)

  // Auto-fetch events for finished games if not in database
  useEffect(() => {
    const shouldFetch = 
      (!query.data || query.data.length === 0) && 
      !query.isLoading && 
      gameStatus === 'finished' && 
      !hasFetched && 
      fixtureId;

    if (shouldFetch) {
      console.log(`🔄 Auto-fetching events for fixture ${fixtureId}`);
      setHasFetched(true);
      
      fetchGameEvents(fixtureId)
        .then(() => {
          console.log(`✅ Events fetched for fixture ${fixtureId}`);
          query.refetch();
        })
        .catch((error) => {
          console.error(`❌ Failed to fetch events for fixture ${fixtureId}:`, error);
        });
    }
  }, [query.data, query.isLoading, gameStatus, hasFetched, fixtureId]);

  // Auto-fetch and start polling for live games
  useEffect(() => {
    if (gameStatus === 'live' && enablePolling && fixtureId && !hasFetched) {
      console.log(`🔴 Live game detected - fetching events for fixture ${fixtureId}`);
      setHasFetched(true);
      
      fetchGameEvents(fixtureId)
        .then(() => {
          console.log(`✅ Live events fetched for fixture ${fixtureId}`);
          query.refetch();
        })
        .catch((error) => {
          console.error(`❌ Failed to fetch live events for fixture ${fixtureId}:`, error);
        });
    }
  }, [gameStatus, enablePolling, fixtureId, hasFetched]);

  return query;
};

// Helper to group events by type
export const groupEventsByType = (events: GameEvent[]) => {
  return {
    goals: events.filter(e => e.event_type === 'Goal'),
    cards: events.filter(e => e.event_type === 'Card'),
    substitutions: events.filter(e => e.event_type === 'subst'),
    var: events.filter(e => e.event_type === 'Var'),
    all: events
  };
};

// Helper to format event time
export const formatEventTime = (event: GameEvent) => {
  if (event.time_extra) {
    return `${event.time_elapsed}+${event.time_extra}'`;
  }
  return `${event.time_elapsed}'`;
};

// Helper to get event icon
export const getEventIcon = (eventType: string, eventDetail: string | null) => {
  // Check for missed penalty first (can be any event type)
  if (eventDetail?.includes('Missed Penalty') || eventDetail?.includes('Penalty Missed')) {
    return '❌';
  }
  
  switch (eventType) {
    case 'Goal':
      if (eventDetail?.includes('Penalty')) return '⚽(P)';
      if (eventDetail?.includes('Own Goal')) return '⚽(OG)';
      return '⚽';
    case 'Card':
      if (eventDetail?.includes('Yellow')) return '🟨';
      if (eventDetail?.includes('Red')) return '🟥';
      return '🟨';
    case 'subst':
      return '🔄';
    case 'Var':
      return '📺';
    default:
      return '•';
  }
};

// Helper to separate events by team
export const separateEventsByTeam = (
  events: GameEvent[], 
  homeTeamId: string | null, 
  awayTeamId: string | null
) => {
  return {
    home: events.filter(e => e.team_id === homeTeamId),
    away: events.filter(e => e.team_id === awayTeamId),
  };
};

// Helper to group events by period
export const groupEventsByPeriod = (events: GameEvent[]) => {
  const firstHalf = events.filter(e => e.time_elapsed <= 45);
  const secondHalf = events.filter(e => e.time_elapsed > 45 && e.time_elapsed <= 90);
  const extraTime = events.filter(e => e.time_elapsed > 90);
  
  return { firstHalf, secondHalf, extraTime };
};
